import * as _ from 'lodash';
import { useEffect } from 'react';
import { ControlLabel, Form, FormControl, FormGroup } from 'react-bootstrap';
import { IAdpRecipientFromChain, ICluster, INetworkEmailSender, IPositionCoordinator, ISignature, IContact } from '@nkr/models/index';
import JoditEditor from '@nkr/components/editor';
import config from '@nkr/views/ticketing/jodit-editor-config';
import StartOffer from './content/start-offer';
import MatchOfferAccepted from './content/match-offer-accepted';
import LogisticsCall from './content/logistics-call';
import KickoffCall from './content/kickoff-call';
import JoinLogisticsCall from './content/join-logistics-call';
import JoinKickoffCall from './content/join-kickoff-call';
import ByCluster from './content/by-cluster';
import ByMissedDeadline from './content/by-missed-deadline';

const CancelKitOrderContent = (props: { message: string, template: string, onMessageChange: (m: string) => void }) => {
    const joditConfig = { ...config, height: '400px' };
    const value = props.message || props.template || '';

    useEffect(() => {
        if (!props.message && props.template) {
            props.onMessageChange(props.template);
        }
    }, [props.template]);

    return (
        <JoditEditor
            value={value}
            config={joditConfig}
            onChange={props.onMessageChange}
            onBlur={props.onMessageChange}
        />
    );
};

interface IProps {
    mode: string,
    sender: string,
    subject: string,
    message: string,
    swapNumber: string,
    force: boolean,
    extra: string[],
    notes: string[],
    swaps: ICluster[],
    adpRecipientsFromChain: IAdpRecipientFromChain[],
    coordinators: IPositionCoordinator[],
    swapManager: IContact,
    deadlineDate: string,
    deadlineTime: string,
    dateTime: string,
    canDoVirtual: boolean,
    signature: ISignature,
    senders: INetworkEmailSender[],
    sidebar: boolean,
    onSenderChange: (sender: string) => void,
    onSubjectChange: (subject: string) => void,
    onMessageChange: (message: string) => void
}

const Message = (props: IProps) => {
    const handleSenderChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        props.onSenderChange(e.currentTarget.value);
    };

    const handleSubjectChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        props.onSubjectChange(e.currentTarget.value);
    };

    const handleMessageChange = (e) => {
        props.onMessageChange(e);
    }

    const { sender, subject, message, senders } = props;

    const groupSenders = _.sortBy(senders.filter(x => x.isIndividual == false), item => item.emailAddress.displayName);
    const individualSenders = _.sortBy(senders.filter(x => x.isIndividual), item => item.emailAddress.displayName);

    return (
        <Form>
            <FormGroup>
                <ControlLabel>From</ControlLabel>
                <FormControl
                    componentClass='select'
                    value={sender}
                    onChange={handleSenderChange}
                >
                    <optgroup label='Groups'>
                        {groupSenders.map((sender, index) =>
                            <option key={'grp-' + index} value={sender.emailAddress.address}>{sender.emailAddress.displayName}</option>
                        )}
                    </optgroup>

                    <optgroup label='Individuals'>
                        {individualSenders.map((sender, index) =>
                            <option key={'ind-' + index} value={sender.emailAddress.address}>{sender.emailAddress.displayName}</option>
                        )}
                    </optgroup>
                </FormControl>
            </FormGroup>

            <FormGroup>
                <ControlLabel>Subject</ControlLabel>
                <FormControl
                    value={subject}
                    onChange={handleSubjectChange}
                />
            </FormGroup>

            <FormGroup>
                <ControlLabel>Content</ControlLabel>
                {props.mode == 'startOffer' && (
                    <StartOffer
                        message={message}
                        force={props.force}
                        extra={props.extra}
                        notes={props.notes}
                        swaps={props.swaps}
                        adpRecipientsFromChain={props.adpRecipientsFromChain}
                        coordinators={props.coordinators}
                        swapManager={props.swapManager}
                        deadlineDate={props.deadlineDate}
                        deadlineTime={props.deadlineTime}
                        includePreselectStatus={true}
                        sidebar={props.sidebar}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'startXm' && (
                    <MatchOfferAccepted
                        message={message}
                        extra={props.extra}
                        swaps={props.swaps}
                        coordinators={props.coordinators}
                        swapManager={props.swapManager}
                        deadlineDate={props.deadlineDate}
                        deadlineTime={props.deadlineTime}
                        canDoVirtual={props.canDoVirtual}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'logisticsCall' && (
                    <LogisticsCall
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        swapNumber={props.swapNumber}
                        dateTime={props.dateTime}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'kickOff' && (
                    <KickoffCall
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        swapNumber={props.swapNumber}
                        dateTime={props.dateTime}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'joinLogisticsCall' && (
                    <JoinLogisticsCall
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'joinKickOff' && (
                    <JoinKickoffCall
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'byCluster' && (
                    <ByCluster
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'cancelSwap' && (
                    <ByCluster
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'byMissedDeadline' && (
                    <ByMissedDeadline
                        message={message}
                        swaps={props.swaps}
                        swapManager={props.swapManager}
                        onMessageChange={(message) => props.onMessageChange(message)}
                    />
                )}

                {props.mode == 'cancelKitOrder' && (
                    <CancelKitOrderContent
                        message={message}
                        template={props.extra && props.extra[0]}
                        onMessageChange={props.onMessageChange}
                    />
                )}

                {props.mode == '' && (
                    <>
                        <JoditEditor
                            value={props.message}
                            config={config}
                            onChange={handleMessageChange}
                            onBlur={() => null}
                        />
                        <br />
                    </>
                )}

                {props.mode == '' && props.signature?.html && (
                    <div dangerouslySetInnerHTML={{ __html: props.signature.html }}></div>
                )}

                {props.signature != null && !(props.mode == '' && props.signature.html) && (
                    <div>
                        <div style={{ fontWeight: 'bold' }}>{props.signature.displayName}</div>

                        {props.signature.titles.map((title, index) =>
                            <div key={'title-' + index}>{title}</div>
                        )}

                        {props.signature.companyName != '' && (
                            <div style={{ fontWeight: 'bold' }} dangerouslySetInnerHTML={{ __html: props.signature.companyName }}></div>
                        )}
                    </div>
                )}
            </FormGroup>
        </Form>
    );
}

export default Message;